import { Graphics, type Sprite } from 'pixi.js';
import { Boss } from './boss-base';
import { getTexture, makeSprite } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import { deflectRing } from '@/entities/bullet-fields';
import type { TelegraphSink } from '@/render/telegraphs';
import { TD, TIDE } from './tide-parts';

export { TIDE } from './tide-parts';

/**
 * B7 — Deep Caller (water Elemental): vortex pull, split/merge water copy,
 * deflecting wave rings, curling waves. Def + tuning in tide-parts.ts.
 */
export class TideBoss extends Boss {
  private vortexTimer = TD.vortexInterval * 0.5;
  private stir = 0; // vortex windup (base class owns `windup`)
  private vortex = 0;
  private time = 0;
  // Split / merge: the lantern stays in the real droplet
  private splitAcc = 0;
  private split = 0;
  private sepX = 0;
  private sepY = 0;
  private sepSign = 1;
  private readonly decoy: Sprite;
  private decoyFlash = 0;
  /** Ship position this step — the copy splits off sideways to it */
  private lastPx = 0;
  private lastPy = 0;
  // Deflecting wave ring
  private waveTimer = TD.waveInterval * 0.6;
  private waveWarn = 0;
  private waveR = -1;
  private readonly waveG = new Graphics();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(TIDE, level, x, y, homeX, homeY);
    this.decoy = makeSprite('boss/tide/body');
    this.decoy.visible = false;
    this.view.addChildAt(this.decoy, 0);
    this.view.addChildAt(this.waveG, 0);
  }

  /** Separation 0..1 — eases out on split, back in on merge. */
  private get spread(): number {
    if (this.split <= 0) return 0;
    const t = TD.splitTime - this.split;
    return Math.min(1, t / TD.splitIn, this.split / TD.mergeOut);
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    this.lastPx = px;
    this.lastPy = py;
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    this.time += dt;
    this.decoyFlash -= dt;
    this.updateVortex(dt, bullets);
    this.updateWave(dt, bullets);
    if (this.split > 0) {
      this.split -= dt;
      if (this.split <= 0) this.splash(bullets, TD.mergeCount, TD.mergeSpeed); // splash
    }
  }

  private updateVortex(dt: number, bullets: BulletSystem): void {
    if (this.vortex > 0) {
      this.vortex -= dt;
      // The undertow lets go with a close-range snap ring
      if (this.vortex <= 0) this.splash(bullets, TD.snapCount, TD.snapSpeed);
    } else if (this.stir > 0) {
      this.stir -= dt;
      if (this.stir <= 0) this.vortex = TD.vortexTime;
    } else {
      this.vortexTimer -= dt;
      if (this.vortexTimer <= 0) {
        this.vortexTimer = TD.vortexInterval / this.levelSpeedMul;
        this.stir = TD.vortexWindup;
      }
    }
  }

  /** Lantern flares → a wave ring rolls out and bounces the player's shots. */
  private updateWave(dt: number, bullets: BulletSystem): void {
    if (this.waveR >= 0) {
      this.waveR += TD.waveSpeed * dt;
      deflectRing(bullets, this.x, this.y, this.waveR, TD.waveBand);
      if (this.waveR > TD.waveMax) this.waveR = -1;
    } else if (this.waveWarn > 0) {
      this.waveWarn -= dt;
      if (this.waveWarn <= 0) this.waveR = this.def.radius;
    } else {
      this.waveTimer -= dt * this.intensity;
      if (this.waveTimer <= 0) {
        this.waveTimer = TD.waveInterval;
        this.waveWarn = TD.waveWarn;
      }
    }
  }

  private splash(bullets: BulletSystem, count: number, speed: number): void {
    for (let i = 0; i < count; i++) {
      const a = (Math.PI * 2 * i) / count;
      bullets.spawn({
        x: this.x, y: this.y, dirX: Math.cos(a), dirY: Math.sin(a),
        speed: speed * this.bulletSpeedMul, life: 3, radius: 10, damage: 1,
        friendly: false, texKey: 'bullet/enemy/ball', rotates: false,
      });
    }
  }

  /** A hard enough beating splits off a water copy (sideways to the ship). */
  override takeDamage(amount: number): boolean {
    const before = this.hp;
    const dead = super.takeDamage(amount);
    if (this.split <= 0 && !dead) {
      this.splitAcc += before - this.hp;
      if (this.splitAcc >= this.maxHp * TD.splitEvery) {
        this.splitAcc = 0;
        this.split = TD.splitTime;
        this.sepSign = -this.sepSign;
        const dx = this.lastPx - this.x;
        const dy = this.lastPy - this.y;
        const d = Math.hypot(dx, dy) || 1;
        this.sepX = (-dy / d) * TD.splitDist * this.sepSign;
        this.sepY = (dx / d) * TD.splitDist * this.sepSign;
      }
    }
    return dead;
  }

  /** The water copy swallows shots without a scratch (no damage numbers). */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    const res = super.absorbBullets(bullets);
    const e = this.spread;
    if (e > 0) {
      bullets.collideCircle(
        true, this.x + this.sepX * e, this.y + this.sepY * e,
        this.def.radius * TD.decoyScale, () => { this.decoyFlash = 0.08; }, false,
      );
    }
    return res;
  }

  /** Vortex pull on the ship (applied by combat/fight-step.ts). */
  override environmentalForce(px: number, py: number): { x: number; y: number } | null {
    if (this.vortex <= 0 || this.dead) return null;
    const dx = this.x - px;
    const dy = this.y - py;
    const d = Math.hypot(dx, dy);
    if (d < this.def.radius + 20 || d > TD.vortexRange) return null;
    const strength = TD.vortexPull * (1 - d / TD.vortexRange);
    return { x: (dx / d) * strength, y: (dy / d) * strength };
  }

  override render(alpha: number): void {
    super.render(alpha);
    const agitated = this.stir > 0 || this.vortex > 0;
    this.body.texture = getTexture('boss/tide/body', agitated ? 1 : 0);
    // Windup shiver — the water trembles before it drinks you in
    if (this.stir > 0) {
      this.body.rotation = Math.sin(this.time * 45) * 0.05;
    } else {
      this.body.rotation = this.vortex > 0 ? Math.sin(this.time * 6) * 0.12 : 0;
    }
    // Lantern flare before a wave ring
    if (this.waveWarn > 0) this.body.tint = TD.lanternTint;
    const e = this.spread;
    this.decoy.visible = e > 0;
    if (e > 0) {
      this.decoy.position.set(this.sepX * e, this.sepY * e);
      this.decoy.texture = this.body.texture;
      this.decoy.rotation = -this.body.rotation;
      this.decoy.tint = TD.decoyTint;
      this.decoy.alpha = TD.decoyAlpha * (this.decoyFlash > 0 ? 1.4 : 1);
      this.decoy.scale.set(TD.decoyScale);
    }
    this.waveG.clear();
    if (this.waveR >= 0) {
      const fade = 1 - this.waveR / TD.waveMax;
      this.waveG.circle(0, 0, this.waveR)
        .stroke({ color: TD.waveColor, width: 12, alpha: 0.35 * fade });
      this.waveG.circle(0, 0, this.waveR)
        .stroke({ color: TD.waveCrest, width: 2.5, alpha: 0.8 * fade });
    }
  }
}
